import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Alert, LinearProgress,
  Stack, Chip, Divider, Grid, TextField, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  Payment as PaymentIcon,
  OpenInNew as ExternalLinkIcon,
  TrendingUp as UsageIcon,
  TrendingDown as TrendingDownIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Receipt as InvoiceIcon,
  CreditCard as CardIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { billingApi, tenantAdminApi } from '../api/services';

const COLORS = {
  primary: '#6C9BFF',
  secondary: '#FF8A65',
  success: '#69F0AE',
  error: '#FF5252',
  warning: '#FFB74D',
  bgPaper: '#121829',
  textPrimary: '#E8ECF4',
  textSecondary: '#8892A8',
  gridLine: '#1E293B',
};

function formatPrice(pence: number, currency?: string): string {
  const amount = pence / 100;
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency || 'GBP' }).format(amount);
  } catch {
    return `${currency || 'GBP'} ${amount.toFixed(2)}`;
  }
}

function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-GB').format(n);
}

interface BillingStatus {
  status: string;
  plan?: string;
  seatCount?: number;
  pricePerSeat?: number;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  stripeCustomerId?: string;
  message?: string;
}

interface UsageData {
  year: number;
  month: number;
  totals: {
    planGenerations: number;
    workItemsCreated: number;
    transcriptsProcessed: number;
    imageAnalyses: number;
    aiTokensUsed: number;
  };
  limits: {
    monthlyAiTokenLimit?: number;
    monthlyTranscriptLimit?: number;
  };
}

function UsageBar({ label, current, limit }: { label: string; current: number; limit?: number }) {
  const percent = limit && limit > 0 ? Math.min((current / limit) * 100, 100) : 0;
  const isHigh = percent > 80;
  const isOverLimit = percent >= 100;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="body2" sx={{ color: COLORS.textPrimary, fontWeight: 500 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ color: isOverLimit ? COLORS.error : isHigh ? COLORS.warning : COLORS.textSecondary }}>
          {formatNumber(current)}{limit ? ` / ${formatNumber(limit)}` : ''}
        </Typography>
      </Stack>
      {limit != null && limit > 0 && (
        <LinearProgress
          variant="determinate"
          value={percent}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: 'rgba(255,255,255,0.06)',
            '& .MuiLinearProgress-bar': {
              bgcolor: isOverLimit ? COLORS.error : isHigh ? COLORS.warning : COLORS.primary,
              borderRadius: 3,
            },
          }}
        />
      )}
    </Box>
  );
}

/** Calculates the projected month-end usage percentage given current usage and day of month. */
function getProjection(current: number, limit: number | undefined): { projectedPercent: number; label: string } | null {
  if (!limit || limit <= 0 || current <= 0) return null;
  const now = new Date();
  const dayOfMonth = now.getDate();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  if (dayOfMonth < 2) return null; // not enough data on day 1
  const dailyRate = current / dayOfMonth;
  const projected = dailyRate * daysInMonth;
  const projectedPercent = Math.round((projected / limit) * 100);
  return { projectedPercent, label: `Projected ${projectedPercent}% by month end` };
}

function QuotaWarningBars({ usage }: { usage: UsageData }) {
  const warnings: { label: string; percent: number; projected: string | null }[] = [];

  const aiLimit = usage.limits.monthlyAiTokenLimit;
  const transcriptLimit = usage.limits.monthlyTranscriptLimit;

  if (aiLimit && aiLimit > 0) {
    const pct = (usage.totals.aiTokensUsed / aiLimit) * 100;
    const proj = getProjection(usage.totals.aiTokensUsed, aiLimit);
    if (pct > 80) {
      warnings.push({ label: 'AI Tokens', percent: Math.round(pct), projected: proj?.label || null });
    }
  }
  if (transcriptLimit && transcriptLimit > 0) {
    const pct = (usage.totals.transcriptsProcessed / transcriptLimit) * 100;
    const proj = getProjection(usage.totals.transcriptsProcessed, transcriptLimit);
    if (pct > 80) {
      warnings.push({ label: 'Transcripts', percent: Math.round(pct), projected: proj?.label || null });
    }
  }

  if (warnings.length === 0) return null;

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      {warnings.map(w => (
        <Alert
          key={w.label}
          severity={w.percent >= 95 ? 'error' : 'warning'}
          icon={<WarningIcon />}
          sx={{
            bgcolor: w.percent >= 95 ? 'rgba(255, 82, 82, 0.08)' : 'rgba(255, 183, 77, 0.08)',
            borderColor: w.percent >= 95 ? 'rgba(255, 82, 82, 0.25)' : 'rgba(255, 183, 77, 0.25)',
          }}
        >
          <strong>{w.label}:</strong> {w.percent}% of monthly quota used
          {w.projected && <Typography variant="caption" sx={{ display: 'block', mt: 0.25 }}>{w.projected}</Typography>}
        </Alert>
      ))}
    </Stack>
  );
}

function UsageProjectionCard({ usage }: { usage: UsageData }) {
  const aiProj = getProjection(usage.totals.aiTokensUsed, usage.limits.monthlyAiTokenLimit);
  const transcriptProj = getProjection(usage.totals.transcriptsProcessed, usage.limits.monthlyTranscriptLimit);

  if (!aiProj && !transcriptProj) return null;

  return (
    <Box sx={{ mt: 2, p: 1.5, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 1, border: '1px solid rgba(255,255,255,0.06)' }}>
      <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 1 }}>
        Month-End Projection
      </Typography>
      <Stack spacing={0.5}>
        {aiProj && (
          <Typography variant="body2" sx={{ color: aiProj.projectedPercent > 100 ? COLORS.error : aiProj.projectedPercent > 80 ? COLORS.warning : COLORS.textPrimary }}>
            AI Tokens: ~{aiProj.projectedPercent}% of limit
          </Typography>
        )}
        {transcriptProj && (
          <Typography variant="body2" sx={{ color: transcriptProj.projectedPercent > 100 ? COLORS.error : transcriptProj.projectedPercent > 80 ? COLORS.warning : COLORS.textPrimary }}>
            Transcripts: ~{transcriptProj.projectedPercent}% of limit
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

function UsageTrendChip({ label, current, previous }: { label: string; current: number; previous: number }) {
  if (previous <= 0) return null;
  const changePercent = Math.round(((current - previous) / previous) * 100);
  if (changePercent === 0) return null;
  const isUp = changePercent > 0;
  return (
    <Chip
      size="small"
      icon={isUp ? <UsageIcon sx={{ fontSize: '14px !important' }} /> : <TrendingDownIcon sx={{ fontSize: '14px !important' }} />}
      label={`${label}: ${isUp ? '+' : ''}${changePercent}% vs last month`}
      sx={{
        bgcolor: isUp ? 'rgba(255, 183, 77, 0.12)' : 'rgba(105, 240, 174, 0.12)',
        color: isUp ? COLORS.warning : COLORS.success,
        fontWeight: 500,
        fontSize: '0.75rem',
        '& .MuiChip-icon': { color: 'inherit' },
      }}
    />
  );
}

export default function TenantBillingPage() {
  const [billingStatus, setBillingStatus] = useState<BillingStatus | null>(null);
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [prevUsage, setPrevUsage] = useState<UsageData | null>(null);
  const [billingEmail, setBillingEmail] = useState('');
  const [billingEmailSaving, setBillingEmailSaving] = useState(false);
  const [billingEmailSuccess, setBillingEmailSuccess] = useState<string | null>(null);

  // Seat management state
  const [tenantInfo, setTenantInfo] = useState<{
    maxExecutiveSeats: number;
    currentExecutives: number;
    maxEaSeats: number;
    monthlyTranscriptLimit: number;
  } | null>(null);
  const [newSeatCount, setNewSeatCount] = useState<number>(1);
  const [seatUpdating, setSeatUpdating] = useState(false);
  const [seatSuccess, setSeatSuccess] = useState<string | null>(null);

  // Cancellation state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const now = new Date();
      const prevMonth = now.getMonth() === 0 ? 12 : now.getMonth();
      const prevYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
      const [statusResult, usageResult, tenantResult, prevUsageResult] = await Promise.allSettled([
        billingApi.getStatus(),
        tenantAdminApi.getUsage(),
        tenantAdminApi.getTenant(),
        tenantAdminApi.getUsage(prevYear, prevMonth),
      ]);

      if (statusResult.status === 'fulfilled') {
        setBillingStatus(statusResult.value);
      }
      if (usageResult.status === 'fulfilled') {
        setUsage(usageResult.value);
      }
      if (tenantResult.status === 'fulfilled') {
        const t = tenantResult.value;
        if (t?.billingEmail) setBillingEmail(t.billingEmail);
        if (t) {
          setTenantInfo({
            maxExecutiveSeats: t.maxExecutiveSeats,
            currentExecutives: t.currentExecutives,
            maxEaSeats: t.maxEaSeats,
            monthlyTranscriptLimit: t.monthlyTranscriptLimit,
          });
          setNewSeatCount(t.maxExecutiveSeats);
        }
      }

      if (prevUsageResult.status === 'fulfilled') {
        setPrevUsage(prevUsageResult.value);
      }

      if (statusResult.status === 'rejected' && usageResult.status === 'rejected') {
        setError('Failed to load billing information.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const result = await billingApi.getPortalUrl();
      if (result.portalUrl) {
        window.open(result.portalUrl, '_blank');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to open billing portal.');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleUpdateBillingEmail = async () => {
    if (!billingEmail.trim()) return;
    setBillingEmailSaving(true);
    setBillingEmailSuccess(null);
    try {
      await tenantAdminApi.updateBillingEmail(billingEmail.trim());
      setBillingEmailSuccess('Billing email updated successfully.');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update billing email.');
    } finally {
      setBillingEmailSaving(false);
    }
  };

  const handleUpdateSeats = async () => {
    if (newSeatCount < 1) return;
    setSeatUpdating(true);
    setSeatSuccess(null);
    setError(null);
    try {
      const result = await tenantAdminApi.updateSeats(newSeatCount);
      setSeatSuccess(result.message || 'Seats updated successfully.');
      if (tenantInfo) {
        setTenantInfo({
          ...tenantInfo,
          maxExecutiveSeats: result.executiveSeats,
          maxEaSeats: result.eaSeats,
          monthlyTranscriptLimit: result.monthlyTranscriptLimit,
        });
      }
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update seats.');
    } finally {
      setSeatUpdating(false);
    }
  };

  const handleOpenPaymentPortal = async () => {
    setPortalLoading(true);
    try {
      const result = await billingApi.getPortalUrl();
      if (result.portalUrl) {
        window.open(result.portalUrl, '_blank');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to open billing portal.');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleCancelTenant = async () => {
    if (!mfaCode.trim()) return;
    setCancelling(true);
    setError(null);
    try {
      await tenantAdminApi.cancelTenant(mfaCode.trim());
      setCancelDialogOpen(false);
      setMfaCode('');
      setCancelSuccess('Subscription cancelled. Your data will be deleted within 24 hours.');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to cancel subscription. Check your MFA code.');
    } finally {
      setCancelling(false);
    }
  };

  const seatDifference = tenantInfo ? newSeatCount - tenantInfo.maxExecutiveSeats : 0;
  const estimatedMonthlyCostChange = billingStatus?.pricePerSeat
    ? (seatDifference * billingStatus.pricePerSeat) / 100
    : null;

  const statusColor = (status?: string) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Trialing': return 'info';
      case 'PastDue': return 'warning';
      case 'Cancelled': return 'error';
      case 'Paused': return 'default';
      default: return 'default';
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <PaymentIcon sx={{ color: 'primary.main', fontSize: 28 }} />
        <Box>
          <Typography variant="h4">Billing & Subscription</Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Manage your plan, view usage, and update payment details
          </Typography>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={2.5}>
        {/* Current Plan Card */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ bgcolor: COLORS.bgPaper, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: COLORS.textPrimary, fontWeight: 600, mb: 2 }}>
                Current Plan
              </Typography>

              {billingStatus?.status === 'NoBilling' ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body1" sx={{ color: COLORS.textSecondary, mb: 2 }}>
                    No billing configured for this tenant.
                  </Typography>
                  <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                    Contact your platform administrator to set up a subscription.
                  </Typography>
                </Box>
              ) : billingStatus ? (
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="h5" sx={{ color: COLORS.primary, fontWeight: 700 }}>
                      {billingStatus.plan || 'Unknown Plan'}
                    </Typography>
                    <Chip
                      label={billingStatus.status}
                      size="small"
                      color={statusColor(billingStatus.status) as any}
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>

                  {billingStatus.pricePerSeat != null && billingStatus.pricePerSeat > 0 && (
                    <Box>
                      <Typography variant="body1" sx={{ color: COLORS.textPrimary }}>
                        {formatPrice(billingStatus.pricePerSeat)} per seat
                      </Typography>
                      <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                        {billingStatus.seatCount || 1} seat{(billingStatus.seatCount || 1) !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  )}

                  <Divider />

                  {billingStatus.currentPeriodEnd && (
                    <Box>
                      <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Current Period
                      </Typography>
                      <Typography variant="body2" sx={{ color: COLORS.textPrimary, mt: 0.5 }}>
                        {billingStatus.currentPeriodStart
                          ? new Date(billingStatus.currentPeriodStart).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                          : '---'
                        }
                        {' to '}
                        {new Date(billingStatus.currentPeriodEnd).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </Typography>
                    </Box>
                  )}

                  {billingStatus.stripeCustomerId && (
                    <Button
                      variant="contained"
                      startIcon={<ExternalLinkIcon />}
                      onClick={handleManageSubscription}
                      disabled={portalLoading}
                      sx={{ mt: 1, alignSelf: 'flex-start' }}
                    >
                      {portalLoading ? 'Opening...' : 'Manage Subscription'}
                    </Button>
                  )}
                </Stack>
              ) : !loading ? (
                <Typography variant="body2" sx={{ color: COLORS.textSecondary, py: 3, textAlign: 'center' }}>
                  Unable to load billing status.
                </Typography>
              ) : null}
            </CardContent>
          </Card>
        </Grid>

        {/* Usage This Month Card */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ bgcolor: COLORS.bgPaper, height: '100%' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 2 }}>
                <UsageIcon sx={{ color: COLORS.warning, fontSize: 20 }} />
                <Typography variant="h6" sx={{ color: COLORS.textPrimary, fontWeight: 600 }}>
                  Usage This Month
                </Typography>
              </Stack>

              {usage ? (
                <Stack spacing={2}>
                  <QuotaWarningBars usage={usage} />
                  {prevUsage && (
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }} useFlexGap>
                      <UsageTrendChip label="AI Tokens" current={usage.totals.aiTokensUsed} previous={prevUsage.totals.aiTokensUsed} />
                      <UsageTrendChip label="Transcripts" current={usage.totals.transcriptsProcessed} previous={prevUsage.totals.transcriptsProcessed} />
                      <UsageTrendChip label="Plans" current={usage.totals.planGenerations} previous={prevUsage.totals.planGenerations} />
                    </Stack>
                  )}
                  <UsageBar
                    label="Plan Generations"
                    current={usage.totals.planGenerations}
                  />
                  <UsageBar
                    label="Transcripts Processed"
                    current={usage.totals.transcriptsProcessed}
                    limit={usage.limits.monthlyTranscriptLimit}
                  />
                  {usage.limits.monthlyTranscriptLimit != null && tenantInfo && (
                    <Typography variant="caption" sx={{ color: COLORS.textSecondary, display: 'block', mt: -1 }}>
                      (Based on {tenantInfo.maxExecutiveSeats} executive{tenantInfo.maxExecutiveSeats !== 1 ? 's' : ''} x 1.5 transcripts/day x 30 days)
                    </Typography>
                  )}
                  <UsageBar
                    label="AI Tokens Used"
                    current={usage.totals.aiTokensUsed}
                    limit={usage.limits.monthlyAiTokenLimit}
                  />
                  <UsageBar
                    label="Image Analyses"
                    current={usage.totals.imageAnalyses}
                  />
                  <UsageBar
                    label="Work Items Created"
                    current={usage.totals.workItemsCreated}
                  />
                  <UsageProjectionCard usage={usage} />
                </Stack>
              ) : !loading ? (
                <Typography variant="body2" sx={{ color: COLORS.textSecondary, py: 3, textAlign: 'center' }}>
                  No usage data available.
                </Typography>
              ) : null}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Manage Seats Card */}
      <Card sx={{ bgcolor: COLORS.bgPaper, mt: 2.5 }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: COLORS.textPrimary, fontWeight: 600, mb: 2 }}>
            Manage Seats
          </Typography>
          {seatSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSeatSuccess(null)}>
              {seatSuccess}
            </Alert>
          )}
          {tenantInfo ? (
            <Stack spacing={2}>
              <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                Current seats: {tenantInfo.maxExecutiveSeats} executive, {tenantInfo.maxEaSeats} EA
                {' '}({tenantInfo.currentExecutives} active executive{tenantInfo.currentExecutives !== 1 ? 's' : ''})
              </Typography>
              <Stack direction="row" alignItems="center" spacing={2}>
                <IconButton
                  size="small"
                  onClick={() => setNewSeatCount(Math.max(1, newSeatCount - 1))}
                  disabled={newSeatCount <= 1 || seatUpdating}
                  sx={{ bgcolor: 'rgba(255,255,255,0.06)' }}
                >
                  <RemoveIcon />
                </IconButton>
                <TextField
                  value={newSeatCount}
                  onChange={e => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val) && val >= 1 && val <= 500) setNewSeatCount(val);
                  }}
                  type="number"
                  size="small"
                  inputProps={{ min: 1, max: 500, style: { textAlign: 'center', width: 60 } }}
                  disabled={seatUpdating}
                />
                <IconButton
                  size="small"
                  onClick={() => setNewSeatCount(Math.min(500, newSeatCount + 1))}
                  disabled={newSeatCount >= 500 || seatUpdating}
                  sx={{ bgcolor: 'rgba(255,255,255,0.06)' }}
                >
                  <AddIcon />
                </IconButton>
                <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                  executive seats
                </Typography>
              </Stack>
              {seatDifference !== 0 && (
                <Box>
                  {estimatedMonthlyCostChange !== null && (
                    <Typography variant="body2" sx={{ color: seatDifference > 0 ? COLORS.warning : COLORS.success }}>
                      {seatDifference > 0 ? 'Adding' : 'Removing'} {Math.abs(seatDifference)} seat{Math.abs(seatDifference) !== 1 ? 's' : ''}
                      {' = '}{seatDifference > 0 ? '+' : '-'}{new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(Math.abs(estimatedMonthlyCostChange))}/mo
                    </Typography>
                  )}
                  {newSeatCount < (tenantInfo?.currentExecutives ?? 0) && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Cannot reduce below {tenantInfo.currentExecutives} seats — you have {tenantInfo.currentExecutives} active executive{tenantInfo.currentExecutives !== 1 ? 's' : ''}.
                    </Alert>
                  )}
                </Box>
              )}
              <Button
                variant="contained"
                onClick={handleUpdateSeats}
                disabled={seatUpdating || seatDifference === 0 || newSeatCount < (tenantInfo?.currentExecutives ?? 0)}
                sx={{ alignSelf: 'flex-start' }}
              >
                {seatUpdating ? 'Updating...' : 'Update Seats'}
              </Button>
            </Stack>
          ) : !loading ? (
            <Typography variant="body2" sx={{ color: COLORS.textSecondary, py: 3, textAlign: 'center' }}>
              Unable to load seat information.
            </Typography>
          ) : null}
        </CardContent>
      </Card>

      {/* Billing Email Card */}
      <Card sx={{ bgcolor: COLORS.bgPaper, mt: 2.5 }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: COLORS.textPrimary, fontWeight: 600, mb: 2 }}>
            Billing Email
          </Typography>
          {billingEmailSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setBillingEmailSuccess(null)}>
              {billingEmailSuccess}
            </Alert>
          )}
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <TextField
              label="Billing Email"
              value={billingEmail}
              onChange={e => setBillingEmail(e.target.value)}
              helperText="This email receives all invoices and billing communications"
              type="email"
              size="small"
              sx={{ flex: 1 }}
            />
            <Button
              variant="contained"
              onClick={handleUpdateBillingEmail}
              disabled={billingEmailSaving || !billingEmail.trim()}
              sx={{ mt: '0 !important', minWidth: 160 }}
            >
              {billingEmailSaving ? 'Saving...' : 'Update Billing Email'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Payment & Invoices Card */}
      {billingStatus?.stripeCustomerId && (
        <Card sx={{ bgcolor: COLORS.bgPaper, mt: 2.5 }}>
          <CardContent>
            <Typography variant="h6" sx={{ color: COLORS.textPrimary, fontWeight: 600, mb: 2 }}>
              Payment & Invoices
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.textSecondary, mb: 2 }}>
              Manage your payment method, view invoices, and download receipts through the Stripe Customer Portal.
            </Typography>
            <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<CardIcon />}
                onClick={handleOpenPaymentPortal}
                disabled={portalLoading}
              >
                {portalLoading ? 'Opening...' : 'Manage Payment Method'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<InvoiceIcon />}
                onClick={handleOpenPaymentPortal}
                disabled={portalLoading}
              >
                {portalLoading ? 'Opening...' : 'View Invoices'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Cancel Subscription Card */}
      {billingStatus?.stripeCustomerId && billingStatus?.status !== 'Cancelled' && (
        <Card sx={{ bgcolor: COLORS.bgPaper, mt: 2.5, border: '1px solid rgba(255, 82, 82, 0.15)' }}>
          <CardContent>
            <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
              <WarningIcon sx={{ color: COLORS.error, fontSize: 20 }} />
              <Typography variant="h6" sx={{ color: COLORS.error, fontWeight: 600 }}>
                Cancel Subscription
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: COLORS.textSecondary, mb: 2 }}>
              Cancelling your subscription will terminate access for all users in your organisation.
              Your data will be permanently deleted within 24 hours. This action cannot be undone.
            </Typography>
            {cancelSuccess && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCancelSuccess(null)}>
                {cancelSuccess}
              </Alert>
            )}
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={() => setCancelDialogOpen(true)}
            >
              Cancel Subscription
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: COLORS.error }}>
          Cancel Subscription
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This will permanently cancel your subscription and all organisation data will be deleted within 24 hours.
            This action cannot be undone.
          </Alert>
          <Typography variant="body2" sx={{ color: COLORS.textSecondary, mb: 2 }}>
            Enter your MFA code to confirm cancellation:
          </Typography>
          <TextField
            label="MFA Code"
            value={mfaCode}
            onChange={e => setMfaCode(e.target.value)}
            size="small"
            fullWidth
            placeholder="Enter 6-digit code from your authenticator app"
            inputProps={{ maxLength: 6, style: { letterSpacing: '0.3em', textAlign: 'center', fontFamily: 'monospace', fontSize: '1.2rem' } }}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCancelDialogOpen(false); setMfaCode(''); }}>
            Keep Subscription
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelTenant}
            disabled={cancelling || mfaCode.trim().length < 6}
          >
            {cancelling ? 'Cancelling...' : 'Confirm Cancellation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Info Footer */}
      <Box sx={{ mt: 3 }}>
        <Alert severity="info" sx={{ bgcolor: 'rgba(108, 155, 255, 0.08)', borderColor: 'rgba(108, 155, 255, 0.2)' }}>
          Need to update your payment method, view invoices, or change your plan? Use the <strong>Manage Payment Method</strong> or <strong>View Invoices</strong> buttons above to access the Stripe Customer Portal.
        </Alert>
      </Box>
    </Box>
  );
}
